import { call, put, select, all, take } from 'redux-saga/effects';
import * as actions from './actions';
import { Base64 } from 'js-base64';
import axios from 'axios';
import { sagas as settingsSaga } from 'store/settings';
import { actions as campaignActions } from 'store/campaign';
import { actions as userMessageActions } from 'store/userMessaging';
import { excludeProductCategories, excludeProductNames, types } from 'constants.js';
import { waitFor } from 'store/helpers';
import { sagas as eventSagas } from 'store/event';
import { AUTO_SAVE_TIMER_MS } from 'constants.js';
import {
  updatePaginatedFilters
} from 'store/helpers/tableAndToolbar/actions';
import { getParams, postMessage, safeBase64Encode, safeBase64Decode } from 'store/helpers/helperFunctions';
import { updateCommunicationFilters } from './actions';
const { waitForGetSupportedEventTypes, waitForGetEventMetaData } = eventSagas;

export function* waitForGetTemplates() {
  return yield waitFor(
    actions.getTemplates(),
    actions.GETTING_TEMPLATES_SUCCEEDED,
    actions.GETTING_TEMPLATES_FAILED
  );
}

// hydration and shape helpers
const decodeTemplateContent = template => {
  if (template.type === 'EMAIL') {
    template.jsonContent = safeBase64Decode(template.jsonContent);
    template.content = safeBase64Decode(template.content);
  } else {
    template.content = safeBase64Decode(template.content);
  }

  return template;
};

const encodeTemplateContent = template => {
  const encodedTemplate = { ...template };

  if (encodedTemplate.type === 'EMAIL') {
    encodedTemplate.content = safeBase64Encode(encodedTemplate.content);
    encodedTemplate.jsonContent = safeBase64Encode(encodedTemplate.jsonContent);
  } else {
    encodedTemplate.content = safeBase64Encode(encodedTemplate.content);
  }

  return encodedTemplate;
};

const setCampaignOccurrenceType = template => {
  if (!template.hasOwnProperty('campaignOccurrenceType')) {
    if (template.schemaId === 'static_scheduled') {
      template.campaignOccurrenceType = 'SCHEDULED';
    } else if (template.schemaId === 'static_onDemand') {
      template.campaignOccurrenceType = 'ON_DEMAND';
    } else {
      template.campaignOccurrenceType = 'ON_EVENT';
    }
  }
  return template;
};

// the actual http calls
const apiRoot = `${window._env_?.REACT_APP_API_PATH}`;

const getTemplateApiRoot = (tenantId, businessContextId) =>
  `${apiRoot}tenants/${tenantId}/context/${businessContextId}/comm-template`;

const getTagsApiRoot = (tenantId, businessContextId) =>
  `${apiRoot}tenants/${tenantId}/context/${businessContextId}/tags/comm-template`;

let isArchiveInProgress = false;

export const getPaginatedTemplatesAsync = (
  tenantId,
  businessContextId,
  urlParams,
  bodyParams
) => {
  return axios
    .post(
      `${getTemplateApiRoot(tenantId, businessContextId)}/search?index=${
        urlParams.index
      }&pageSize=${urlParams.pageSize}`,
      bodyParams
    )
    .then(response => ({
      data: response.data,
      totalRecords: parseInt(response.headers['x-ag-totalcount']) || 0
    }))
    .catch(err => {
      throw err;
    });
};

export const getMinimalTemplatesAsync = (
  tenantId,
  businessContextId,
  urlParams,
  bodyParams
) => {
  return axios
    .post(
      `${getTemplateApiRoot(tenantId, businessContextId)}/advance-search?index=${
        urlParams.index
      }&pageSize=${urlParams.pageSize}`,
      bodyParams
    )
    .then(response => ({
      data: response.data,
      totalRecords: parseInt(response.headers['x-ag-totalcount'])
    }))
    .catch(err => {
      throw err;
    });
};

export const getTemplatesAsync = (tenantId, businessContextId, templateUuid) =>
  axios
    .get(
      `${getTemplateApiRoot(tenantId, businessContextId)}${templateUuid ? `/uuid/${templateUuid}` : ''
      }`
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const getTemplateDetailsAsync = (
  tenantId,
  businessContextId,
  communicationTemplateUuids
) => {
  return axios
    .post(
      `${getTemplateApiRoot(tenantId, businessContextId)}/uuid/bulk`,
      communicationTemplateUuids
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });
};

//deprecated
export const getTemplatesAsyncWithId = (tenantId, businessContextId, templateId) =>
  axios
    .get(
      `${getTemplateApiRoot(tenantId, businessContextId)}${templateId ? `/${templateId}` : ''
      }`
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const getTagsAsync = (tenantId, businessContextId) =>
  axios
    .get(`${getTagsApiRoot(tenantId, businessContextId)}`)
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const createTemplateAsync = (tenantId, businessContextId, template) =>
  axios
    .post(`${getTemplateApiRoot(tenantId, businessContextId)}`, template)
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const testTemplateAsync = (
  tenantId,
  businessContextId,
  testTemplateData
) =>
  axios
    .post(
      `${getTemplateApiRoot(tenantId, businessContextId)}/verify`,
      testTemplateData
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const updateTemplateAsync = (tenantId, businessContextId, template) =>
  axios
    .put(
      `${getTemplateApiRoot(tenantId, businessContextId)}/uuid/${template.uuid}`,
      template
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

//deprecated
export const updateTemplateWithIdAsync = (tenantId, businessContextId, template) =>
  axios
    .put(
      `${getTemplateApiRoot(tenantId, businessContextId)}/${template.id}`,
      template
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const deleteTemplateAsync = (tenantId, businessContextId, templateUuid) =>
  axios
    .delete(`${getTemplateApiRoot(tenantId, businessContextId)}/uuid/${templateUuid}`)
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

//deprecated
export const deleteTemplateAsyncWithId = (tenantId, businessContextId, templateId) =>
  axios
    .delete(`${getTemplateApiRoot(tenantId, businessContextId)}/${templateId}`)
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

// sagas

// selectors
const currentTenantId = store => store.user.selectedTenant.tenantId;
const currentBusinessContextId = store =>
  store.user.selectedBusinessContext.businessContextId;
const currentTemplate = store => (store.template ? store.template.current : {});
const templateList = store => (store.template ? store.template.list : []);
const paginatedTemplateList = store =>
  store.template ? store.template.paginatedList : [];
const minimalTemplateList = store =>
  store.template ? store.template.minimalList : [];
const minimalTotalRecords = store =>
  store.template ? store.template.minimalTotalRecords : 0;
const productNames = store => store.events.supportedEventTypes.productNames;
const productCategories = store =>
  store.events.supportedEventTypes.productCategories;
const tags = store => (store.template ? store.template.tagList : []);
const searchInput = store =>
  store.template.filters ? store.template.filters?.searchInput : '';
const displayActive = store =>
  store.template.filters ? store.template.filters?.displayActive : true;
const sortBy = store =>
  store.template.filters ? store.template.filters?.sortBy : 'name';
const sortOrder = store =>
  store.template.filters ? store.template.filters?.sortOrder : 'ASC';
const rowsPerPage = store =>
  store.template.filters ? store.template.filters?.rowsPerPage : 10;
const pageNo = store =>
  store.template.filters ? store.template.filters?.pageNo : 0;
const totalCount = store =>
  store.template.filters ? store.template.filters?.totalCount : 0;
const pageNoFilter = store =>
  store.tableAndToolbar ? store.tableAndToolbar.paginatedFilters.pageNo : 0;
const searchInputFilter = store =>
  store.tableAndToolbar ? store.tableAndToolbar.paginatedFilters.searchInput : '';
const rowsPerPageFilter = store =>
  store.tableAndToolbar ? store.tableAndToolbar.paginatedFilters.rowsPerPage : 10;
const filterTypes = store =>
  store.template.filters ? store.template.filters?.filterTypes : [];
const filterTemplates = store =>
  store.template.filters ? store.template.filters?.filterTemplates : [];
const filterProductNames = store =>
  store.template.filters ? store.template.filters?.filterProductNames : [];
const filterProductCategories = store =>
  store.template.filters ? store.template.filters?.filterProductCategories : [];
const filterMarketingTypes = store =>
  store.template.filters ? store.template.filters?.filterMarketingTypes : [];
const filterTags = store =>
  store.template.filters ? store.template.filters?.filterTags : [];
const filterStatus = store =>
  store.template.filters ? store.template.filters?.filterStatus : [];
const selectedTemplateNamesByUuid = store =>
  store.template ? store.template.selectedNamesByUuid : {};
const totalCountFilter = store =>
  store.tableAndToolbar ? store.tableAndToolbar.paginatedFilters.totalCount : 0;
const activeFilter = store =>
  store.tableAndToolbar.paginatedFilters ? store.tableAndToolbar.paginatedFilters?.activeTemplateFilter : null;

// helper saga to prevent culling the saga tree on rejection of a parallelized task.
function* reflect(callArgs) {
  try {
    return { value: yield call(...callArgs), status: 'resolved' };
  } catch (ex) {
    return ex;
  }
}

export function* getTemplatesIfNeeded(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let currentList = yield select(templateList);
    if (
      action.type === actions.GET_TEMPLATES &&
      (currentList.length === 0 || payload.force === true)
    ) {
      yield put(actions.gettingTemplates());
      yield call(waitForGetSupportedEventTypes);
      currentList = yield call(getTemplatesAsync, tenantId, businessContextId);
      currentList = currentList.map(decodeTemplateContent);
      currentList = currentList.map(setCampaignOccurrenceType);
    }

    yield put(actions.gettingTemplatesSucceeded(currentList));
  } catch (ex) {
    yield put(actions.gettingTemplatesFailed(ex));
  }
}

export function* getPaginatedTemplatesIfNeeded(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let currentList = yield select(paginatedTemplateList);
    yield call(waitForGetSupportedEventTypes);
    let productCategory = yield select(productCategories);
    let productName = yield select(productNames);

    if (
      action.type === actions.GET_PAGINATED_TEMPLATES &&
      (currentList.length === 0 || payload.force === true)
    ) {
      const activeFilterValue = yield select(activeFilter);
      const campaignContext = payload.campaignContext || {};
      const hasCampaignContext = !!payload.campaignContext;
      const selectedStatusFilters = yield select(filterStatus);
      const activeValueFromStatusFilters =
        Array.isArray(selectedStatusFilters) && selectedStatusFilters.length === 1
          ? (selectedStatusFilters.includes('active')
              ? true
              : (selectedStatusFilters.includes('archived') ? false : null))
          : null;
      
      // Determine active filter value from payload for isFilter mode
      const getActiveValueFromPayload = () => {
        const { displayActive, displayArchived } = payload;
        // If only Active is selected, return true
        if (displayActive === true && displayArchived !== true) {
          return true;
        }
        // If only Archived is selected, return false
        if (displayArchived === true && displayActive !== true) {
          return false;
        }
        // If both or neither are selected, return null (don't filter)
        return null;
      };
      
      const payloadActiveValue = payload.isFilter ? getActiveValueFromPayload() : null;
      
      // Get searchScopes from payload for isFilter mode (hierarchy filter)
      const payloadSearchScopes = payload.isFilter && Array.isArray(payload.searchScopes) && payload.searchScopes.length > 0 
        ? payload.searchScopes 
        : null;
      
      // Get tags from payload for isFilter mode (tags filter)
      const payloadTags = payload.isFilter && Array.isArray(payload.tags) && payload.tags.length > 0 
        ? payload.tags 
        : null;
      
      let requestBody = payload.isFilter === true ?
      {
        name: yield select(searchInputFilter),
        sortBy: 'name',
        sortOrder: 'ASC',
        type: yield select(filterTypes),
        productName: yield select(filterProductNames),
        productCategory: yield select(filterProductCategories),
        ...(payloadActiveValue !== null ? { active: payloadActiveValue } : (activeFilterValue !== null && { active: activeFilterValue })),
        ...(payloadSearchScopes && { searchScopes: payloadSearchScopes }),
        ...(payloadTags && { tags: payloadTags })
      } :
      {
        name: yield select(searchInput),
        sortBy: yield select(sortBy),
        sortOrder: yield select(sortOrder),
        type: yield select(filterTypes),
        searchScopes: yield select(filterTemplates),
        tags: yield select(filterTags),
        productName: yield select(filterProductNames),
        productCategory: yield select(filterProductCategories),
        ...(hasCampaignContext
          ? (activeValueFromStatusFilters !== null
              ? { active: activeValueFromStatusFilters }
              : {})
          : { active: yield select(displayActive) }),
        ...(campaignContext.productType && {
          productType: campaignContext.productType
        }),
        ...(campaignContext.subscriberListId && {
          subscriberListId: campaignContext.subscriberListId
        }),
        ...(campaignContext.segmentedListId && {
          segmentedListId: campaignContext.segmentedListId
        }),
        ...(campaignContext.eventTypeCategory && {
          eventTypeCategory: Array.isArray(campaignContext.eventTypeCategory)
            ? campaignContext.eventTypeCategory
            : [campaignContext.eventTypeCategory]
        }),
        promotional:
          typeof campaignContext.promotional === 'boolean'
            ? campaignContext.promotional
            : yield select(filterMarketingTypes),
        ...(hasCampaignContext && {
          surveyResponseTemplate:
            campaignContext.campaignOccurrenceType === 'INTERNAL_SURVEY_RESPONSE' ||
            campaignContext.campaignOccurrenceType === 'EXTERNAL_SURVEY_RESPONSE'
              ? true
              : false
        })
      };
      let urlParams = payload.isFilter === true ?
      {
        index: yield select(pageNoFilter),
        pageSize: yield select(rowsPerPageFilter)
      } :
      {
        index: yield select(pageNo),
        pageSize: yield select(rowsPerPage)
      };
      let bodyParams = {
        ...getParams(requestBody, productName, productCategory)
      };
      if (payload.excludeListType) {
        bodyParams = {
          ...bodyParams,
          productName: payload.filteredProductName.length > 0 ? payload.filteredProductName : bodyParams.productName.filter(name => !excludeProductNames.includes(name)),
          productCategory: bodyParams.productCategory.filter(category => !excludeProductCategories.includes(category))
        }
      }
      let listCount = yield select(payload.isFilter === true ? totalCountFilter : totalCount);
      if (
        isArchiveInProgress &&
        listCount - 1 &&
        urlParams.index >= (listCount - 1) / urlParams.pageSize
      ) {
        urlParams.index = urlParams.index - 1;
        yield put(payload.isFilter === true ? updatePaginatedFilters({ pageNo: urlParams.index }) : updateCommunicationFilters({ pageNo: urlParams.index }));
      }
      yield put(actions.gettingPaginatedTemplates());
      currentList = yield call(
        getPaginatedTemplatesAsync,
        tenantId,
        businessContextId,
        urlParams,
        bodyParams
      );
      // Update totalCount in both stores - paginatedFilters for FilterBar and template.filters for MultiSelectDropdown
      if (payload.isFilter === true) {
        yield put(updatePaginatedFilters({ totalCount: currentList.totalRecords }));
        yield put(updateCommunicationFilters({ totalCount: currentList.totalRecords }));
      } else {
        yield put(updateCommunicationFilters({ totalCount: currentList.totalRecords }));
      }
      const responseData = currentList?.data;
      const templateList = Array.isArray(responseData)
        ? responseData
        : (Array.isArray(responseData?.data) ? responseData.data : []);

      currentList = templateList
        .map(decodeTemplateContent)
        .map(setCampaignOccurrenceType);
    }
    isArchiveInProgress = false;
    yield put(actions.gettingPaginatedTemplatesSucceeded(currentList));
  } catch (ex) {
    isArchiveInProgress = false;
    yield put(updateCommunicationFilters({ totalCount: 0 }));
    yield put(actions.gettingPaginatedTemplatesFailed(ex));
  }
}

export function* getMinimalTemplatesIfNeeded(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = payload.businessContextId || (yield select(currentBusinessContextId));
    
    if (action.type === actions.GET_MINIMAL_TEMPLATES) {
      yield put(actions.gettingMinimalTemplates());
      
      let urlParams = {
        index: payload.index || 0,
        pageSize: payload.pageSize || 100
      };
      
      let bodyParams = {
        active: payload.active !== undefined ? payload.active : true,
        requiredFields: payload.requiredFields || [],
        ...(payload.productCategory && { productCategory: payload.productCategory }),
        ...(payload.productName && { productName: payload.productName }),
        ...(payload.eventTypeCategory && { eventTypeCategory: payload.eventTypeCategory }),
        ...(payload.name && { name: payload.name })
      };
      
      let currentList = yield call(
        getMinimalTemplatesAsync,
        tenantId,
        businessContextId,
        urlParams,
        bodyParams
      );
      
      yield put(actions.gettingMinimalTemplatesSucceeded({
        data: currentList.data,
        totalRecords: currentList.totalRecords
      }));
    }
  } catch (ex) {
    yield put(actions.gettingMinimalTemplatesFailed(ex));
  }
}

export function* getTemplateNamesByUuidsIfNeeded(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId =
      payload.businessContextId || (yield select(currentBusinessContextId));
    const existingNamesByUuid = yield select(selectedTemplateNamesByUuid);

    const uniqueUuids = Array.from(
      new Set((payload.templateUuids || []).filter(Boolean))
    );
    const missingUuids = uniqueUuids.filter(
      uuid => !existingNamesByUuid?.[uuid]
    );

    if (missingUuids.length === 0) {
      yield put(actions.gettingTemplateNamesByUuidsSucceeded({}));
      return;
    }

    yield put(actions.gettingTemplateNamesByUuids());

    const templateResponses = yield call(
      getTemplateDetailsAsync,
      tenantId,
      businessContextId,
      missingUuids
    );

    const namesByUuid = (templateResponses || []).reduce((result, template) => {
      if (template?.uuid && template?.name) {
        result[template.uuid] = template.name;
      }
      return result;
    }, {});

    yield put(actions.gettingTemplateNamesByUuidsSucceeded(namesByUuid));
  } catch (ex) {
    yield put(actions.gettingTemplateNamesByUuidsFailed(ex));
  }
}

export function* getTagsIfNeeded(action) {
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let tagList = yield select(tags);

    if (action.type === actions.GET_TEMPLATE_TAGS) {
      yield put(actions.gettingTemplateTags());
      tagList = yield call(getTagsAsync, tenantId, businessContextId);
    }
    yield put(actions.gettingTemplateTagsSucceeded(tagList));
  } catch (ex) {
    yield put(actions.gettingTemplateTagsFailed(ex));
  }
}

export function* getTemplateIfNeeded(action) {
  const { payload } = action;
  try {
    let yieldAlls = {
      supportedEventTypes: yield call(waitForGetSupportedEventTypes)
    };

    yield put(actions.gettingTemplate());

    let tenantId = yield select(currentTenantId);
    let businessContextId = payload.businessContextId || (yield select(currentBusinessContextId));
    let template = yield select(currentTemplate);
    let templateUuid = payload.templateUuid;

    if (
      templateUuid &&
      ((template.uuid !== templateUuid && !(template._isCopy || template._isCopyTemplate)) ||
        payload.force === true)
    ) {
      template = yield call(
        getTemplatesAsync,
        tenantId,
        businessContextId,
        templateUuid
      );

      template = decodeTemplateContent(template);
      template = setCampaignOccurrenceType(template);
    }

    let eventMetaData, metaDataPath;
    if (template.schemaIdentifier && !payload.skipMetadata) {
      ({ eventMetaData, metaDataPath } = yield call(waitForGetEventMetaData, {
        campaignOccurrenceType: template.campaignOccurrenceType,
        schemaIdentifier: template.schemaIdentifier,
        productName: template.productName,
        workflow: 'template',
        productType: template.productType,
        languageCode: template?.languageCode || null
      }));
    }

    if (!template.productCategory || `${template.productCategory}`.trim() === '') {
      const derivedProductCategory =
        metaDataPath?.[0] ||
        (typeof template.schemaIdentifier === 'string'
          ? template.schemaIdentifier.split('.')?.[0]
          : '');
      template = { ...template, productCategory: derivedProductCategory || '' };
    }

     if (!template.hasOwnProperty('promotional')) {
        template = { ...template, promotional: false };
      }

    yield put(actions.gettingTemplateSucceeded({ template, eventMetaData }));
  } catch (ex) {
    yield put(actions.gettingTemplateFailed(ex));
  }
}

export function* createTemplateFromCurrent() {
  try {
    yield put(actions.creatingTemplate());
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let template = yield select(currentTemplate);
    let eventMetaData = template.eventMetaData;

    if (!template.uuid) {
      template = encodeTemplateContent(template);
      if (template.type == 'SMS' && template.hasOwnProperty('layout')) {
        delete template.layout;
      }

      template = yield call(
        createTemplateAsync,
        tenantId,
        businessContextId,
        template
      );
    }
    postMessage(template);
    template = decodeTemplateContent(template);
    template = { ...template, eventMetaData: eventMetaData };

    yield put(actions.creatingTemplateSucceeded(template));
  } catch (ex) {
    yield put(actions.creatingTemplateFailed(ex));
  }
}

export function* updateTemplateFromCurrent() {
  try {
    yield put(actions.updatingTemplate());
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let template = yield select(currentTemplate);
    let eventMetaData = template.eventMetaData;
    if (template.uuid) {
      template = encodeTemplateContent(template);

      template = yield call(
        updateTemplateAsync,
        tenantId,
        businessContextId,
        template
      );
    }

    template = decodeTemplateContent(template);
    template = { ...template, eventMetaData: eventMetaData };

    yield put(actions.updatingTemplateSucceeded(template));
  } catch (ex) {
    yield put(actions.updatingTemplateFailed(ex));
  }
}

export function* warnAndArchive(action) {
  yield put(
    campaignActions.getAssociatedCampaignsFor({
      uuid: action.payload.uuid,
      typeOf: types.TEMPLATE
    })
  );
  yield take(actions.GETTING_ASSOCIATED_CAMPAIGNS_FOR_TEMPLATE_SUCCEEDED);
  let templates = yield select(paginatedTemplateList);
  let templateToWarnAbout = templates.find(
    template => template.uuid === action.payload.uuid
  );
  action.payload.message.props = {
    ...action.payload.message.props,
    template: templateToWarnAbout,
    type: 'template'
  };
  yield put(userMessageActions.setMessage(action.payload.message));
}

export function* deleteTemplatesByIdList(action) {
  const { payload } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let templateUuids = payload.templateUuids;
    let templates = yield select(paginatedTemplateList);
    let deletedTemplateStatuses = [];
    if (templateUuids.length > 0) {
      yield put(actions.deletingTemplates());
      // all() prallelizes delete operations and waits until they all finish.
      // if the delete template API method can be made to return the deleted id this could be set to a variable and report partial success which would make for better failure handling.
      if (payload.hardDelete) {
        deletedTemplateStatuses = yield all(
          templateUuids
            .map(templateUuid => [
              deleteTemplateAsync,
              tenantId,
              businessContextId,
              templateUuid
            ])
            .map(reflect)
        );
      } else {
        deletedTemplateStatuses = yield all(
          templateUuids
            .map(templateUuid => {
              const template = templates.find(t => t.uuid === templateUuid);
              const encodedTemplate = encodeTemplateContent({ ...template });
              return [
                updateTemplateAsync,
                tenantId,
                businessContextId,
                {
                  ...encodedTemplate,
                  active: !payload.activeStatus
                }
              ];
            })
            .map(reflect)
        );
      }
    }
    if (
      deletedTemplateStatuses.filter(status => status.status === 'resolved')
        .length === templateUuids.length
    ) {
      isArchiveInProgress = true;
      yield all([
        put(
          actions.deletingTemplatesSucceeded(templateUuids, payload.hardDelete)
        ),
        put(actions.getPaginatedTemplates({ force: true }))
      ]);
    } else {
      yield put(
        actions.deletingTemplatesFailed({
          message: 'Not all requested templates could be deleted.',
          deletedTemplateStatuses
        })
      );
    }
  } catch (ex) {
    isArchiveInProgress = false;
    yield put(
      actions.deletingTemplatesFailed({
        ...ex,
        hardDelete: payload.hardDelete
      })
    );
  }
}

export function* testTemplate() {
  try {
    yield put(actions.testingTemplate());
    let template = yield select(currentTemplate);
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);

    template = encodeTemplateContent({ ...template });

    let testedResponse = yield call(
      testTemplateAsync,
      tenantId,
      businessContextId,
      template
    );

    yield put(actions.testingTemplateSucceeded(testedResponse));
  } catch (ex) {
    yield put(actions.testingTemplateFailed(ex));
  }
}

let timer;
export function* autoSaveTimerStart() {
  if (timer) {
    clearTimeout(timer);
  }

  yield call(
    () =>
      new Promise(resolve => {
        timer = setTimeout(resolve, AUTO_SAVE_TIMER_MS);
      })
  );

  yield put(actions.templateAutoSaveTimerEnd());
}
